import React, { useEffect } from 'react';
import { App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { checkPendingContent } from '@/services/ant-design-pro/abnormalContent';

interface PendingContentCheckerProps {
  currentUser?: API.CurrentUser;
}

const PendingContentChecker: React.FC<PendingContentCheckerProps> = ({ currentUser }) => {
  const { modal } = App.useApp();

  useEffect(() => {
    // 只有在用户已登录的情况下才进行检查
    // 注意：不再在前端判断是否是超级管理员，完全依赖后端返回的数据
    // 后端会为超级管理员返回 has_pending: false, count: 0
    if (currentUser) {
      // 检查是否是从登录页面跳转过来的（通过URL参数判断）
      const urlParams = new URLSearchParams(window.location.search);
      const isFromLogin = urlParams.get('from') === 'login' ||
                         document.referrer.includes('/user/login') ||
                         !sessionStorage.getItem('hasLoggedInThisSession');

      if (isFromLogin) {
        // 如果是登录后首次进入，清除所有提醒标记并标记已登录
        sessionStorage.removeItem('pendingContentModalShownThisSession');
        sessionStorage.removeItem('pendingContentRemindLaterUntil'); // 清除稍后提醒标记
        sessionStorage.setItem('hasLoggedInThisSession', 'true');
      }

      // 添加短暂延迟，确保页面完全加载后再检查
      const timer = setTimeout(() => {
        checkForPendingContent();
      }, 1000);

      return () => clearTimeout(timer);
    }
    // 如果没有用户，不需要返回清理函数
    return undefined;
  }, [currentUser]);

  const checkForPendingContent = async () => {
    try {
      const response = await checkPendingContent();

      if (response.has_pending && response.count > 0) {
        // 检查是否已经显示过提醒（防止页面跳转后重复显示）
        const hasShownModalThisSession = sessionStorage.getItem('pendingContentModalShownThisSession');

        // 如果已经显示过提醒，则跳过
        if (hasShownModalThisSession === 'true') {
          return;
        }

        // 标记已显示提醒
        sessionStorage.setItem('pendingContentModalShownThisSession', 'true');

        // 稍后提醒的截止时间（毫秒时间戳）
        const remindLaterUntil = Number(sessionStorage.getItem('pendingContentRemindLaterUntil') || '0');
        const now = Date.now();
        if (remindLaterUntil && now < remindLaterUntil) {
          return; // 还在“稍后提醒”窗口内，跳过弹窗
        }

        // 显示弹窗提示（支持"稍后提醒"）
        modal.confirm({
          title: '待处理内容提醒',
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          content: response.message,
          okText: '立即处理',
          cancelText: '稍后提醒',
          onOk: () => {
            // 跳转到异常内容页面
            window.location.href = '/content/abnormal';
          },
          onCancel: () => {
            // 用户选择稍后提醒：10分钟内不再弹出
            const tenMinutes = 10 * 60 * 1000;
            sessionStorage.setItem('pendingContentRemindLaterUntil', String(Date.now() + tenMinutes));
          },
        });
      }
    } catch (error) {
      console.error('检查待处理内容失败:', error);
      // 静默处理错误，不影响用户体验
    }
  };

  return null; // 这个组件不渲染任何内容
};

export default PendingContentChecker;
